import { api } from "@/lib/api";
import type { ApiResponse } from "@learn-me/types";

export interface ChapterSection {
  id: string;
  title: string;
  content: string;
  audioUrl?: string;
  audioDuration?: number;
  position: number;
}

interface ChapterDetail {
  id: string;
  title: string;
  description?: string;
  content?: string;
  videoUrl?: string;
  videoDuration?: number;
  audioUrl?: string;
  audioPublicId?: string;
  audioDuration?: number;
  audioVoice?: string;
  audioGeneratedAt?: string;
  order: number;
  isFree: boolean;
  isPublished: boolean;
  attachments: Array<{
    id: string;
    name: string;
    url: string;
    type: string;
    size?: number;
  }>;
  sections?: ChapterSection[];
  quiz?: {
    id: string;
    title: string;
    passingScore: number;
    questions: Array<{
      id: string;
      text: string;
      type: string;
      options: Array<{
        id: string;
        text: string;
      }>;
    }>;
  };
  course: {
    id: string;
    title: string;
    slug: string;
  };
  userProgress?: {
    isCompleted: boolean;
    completedAt?: string;
    lastPosition?: number;
    videoProgress?: number;
  } | null;
}

interface VideoProgressData {
  lastPosition: number;
  videoProgress?: number;
}

interface VideoProgressResult {
  id: string;
  lastPosition: number;
  videoProgress: number;
  isCompleted: boolean;
}

interface NewBadge {
  badgeId: string;
  name: string;
  icon: string;
}

interface QuizAttemptResult {
  attempt: {
    id: string;
    score: number;
    passed: boolean;
    startedAt: string;
    completedAt: string;
  };
  results: Array<{
    questionId: string;
    isCorrect: boolean;
    points: number;
    explanation?: string;
  }>;
  score: number;
  passed: boolean;
  totalPoints: number;
  earnedPoints: number;
  passingScore: number;
  newBadges?: NewBadge[];
}

interface MarkCompleteResult {
  courseProgress: number;
  isCourseComplete: boolean;
  newBadges?: NewBadge[];
}

interface SectionProgressItem {
  sectionId: string;
  position: number;
  isViewed: boolean;
  viewedAt: string | null;
  timeSpent: number;
  requiredSeconds: number;
  remainingSeconds: number;
}

interface ChapterSectionProgress {
  sections: SectionProgressItem[];
  totalSections: number;
  viewedSections: number;
  progressPercent: number;
  lastViewedSectionPosition: number | null;
}

interface SectionGatingInfo {
  maxAccessiblePosition: number;
  requiresSequential: boolean;
}

// Payment Status Types
export type PaymentStatus =
  | "PENDING"
  | "HELD"
  | "COMPLETED"
  | "FAILED"
  | "REFUNDED";

export interface PaymentInfo {
  paymentStatus: PaymentStatus | null;
  paymentMessage: string | null;
  paymentHoldReleaseDate: string | null;
  hasPaymentAccess: boolean;
}

interface ChaptersWithPaymentInfo extends PaymentInfo {
  chapters: ChapterDetail[];
}

interface ChapterDetailWithPayment extends ChapterDetail, PaymentInfo {}

export const learningApi = {
  async getChapter(chapterId: string): Promise<ChapterDetailWithPayment> {
    const response = await api.get<ApiResponse<ChapterDetailWithPayment>>(
      `/chapters/${chapterId}`,
    );
    return response.data.data!;
  },

  async getChaptersByCourse(
    courseId: string,
  ): Promise<ChaptersWithPaymentInfo> {
    const response = await api.get<ApiResponse<ChaptersWithPaymentInfo>>(
      `/chapters/course/${courseId}`,
    );
    return response.data.data!;
  },

  async markChapterComplete(chapterId: string): Promise<MarkCompleteResult> {
    const response = await api.post<ApiResponse<MarkCompleteResult>>(
      `/chapters/${chapterId}/complete`,
    );
    return response.data.data!;
  },

  async updateVideoProgress(
    chapterId: string,
    data: VideoProgressData,
  ): Promise<VideoProgressResult> {
    const response = await api.put<ApiResponse<VideoProgressResult>>(
      `/chapters/${chapterId}/progress`,
      data,
    );
    return response.data.data!;
  },

  async startQuizAttempt(quizId: string): Promise<{
    id: string;
    startedAt?: string;
    savedAnswers?: Record<string, string[]> | null;
  }> {
    const response = await api.post<
      ApiResponse<{
        id: string;
        startedAt?: string;
        savedAnswers?: Record<string, string[]> | null;
      }>
    >(`/quizzes/${quizId}/start`);
    return response.data.data!;
  },

  async saveQuizProgress(
    attemptId: string,
    savedAnswers: Record<string, string[]>,
  ): Promise<{ lastSavedAt: string }> {
    const response = await api.post<ApiResponse<{ lastSavedAt: string }>>(
      `/quizzes/attempts/${attemptId}/save-progress`,
      { savedAnswers },
    );
    return response.data.data!;
  },

  async submitQuizAttempt(
    attemptId: string,
    answers: Array<{ questionId: string; selectedOptionIds: string[] }>,
  ): Promise<QuizAttemptResult> {
    const response = await api.post<ApiResponse<QuizAttemptResult>>(
      `/quizzes/attempts/${attemptId}/submit`,
      { answers },
    );
    return response.data.data!;
  },

  async getQuizByChapter(chapterId: string) {
    const response = await api.get<
      ApiResponse<{
        id: string;
        title: string;
        passingScore: number;
        timeLimit?: number;
        maxAttempts?: number;
        cooldownMinutes?: number;
        shuffleQuestions?: boolean;
        shuffleOptions?: boolean;
        questions: Array<{
          id: string;
          text: string;
          type: string;
          options: Array<{
            id: string;
            text: string;
          }>;
        }>;
        userAttempts?: Array<{
          id: string;
          score: number;
          passed: boolean;
          completedAt: string;
        }>;
        bestScore?: number;
        // Anti-cheating info
        attemptsRemaining?: number | null;
        cooldownEndsAt?: string | null;
        canStartAttempt?: boolean;
      }>
    >(`/quizzes/chapter/${chapterId}`);
    return response.data.data!;
  },

  // Section Progress Functions
  async markSectionViewed(
    sectionId: string,
  ): Promise<{ isViewed: boolean; viewedAt: string }> {
    const response = await api.post<
      ApiResponse<{ isViewed: boolean; viewedAt: string }>
    >(`/sections/${sectionId}/viewed`);
    return response.data.data!;
  },

  async updateSectionTimeSpent(
    sectionId: string,
    seconds: number,
  ): Promise<{
    timeSpent: number;
    requiredSeconds: number;
    remainingSeconds: number;
    isViewed: boolean;
    viewedAt: string | null;
  }> {
    const response = await api.post<
      ApiResponse<{
        timeSpent: number;
        requiredSeconds: number;
        remainingSeconds: number;
        isViewed: boolean;
        viewedAt: string | null;
      }>
    >(`/sections/${sectionId}/time`, { seconds });
    return response.data.data!;
  },

  async getChapterSectionProgress(
    chapterId: string,
  ): Promise<ChapterSectionProgress> {
    const response = await api.get<ApiResponse<ChapterSectionProgress>>(
      `/sections/chapter/${chapterId}/progress`,
    );
    return response.data.data!;
  },

  async getResumePosition(
    chapterId: string,
  ): Promise<{ position: number } | null> {
    const response = await api.get<ApiResponse<{ position: number } | null>>(
      `/sections/chapter/${chapterId}/resume`,
    );
    return response.data.data ?? null;
  },

  // Section Gating Functions
  async getSectionGating(chapterId: string): Promise<SectionGatingInfo> {
    const response = await api.get<ApiResponse<SectionGatingInfo>>(
      `/sections/chapter/${chapterId}/gating`,
    );
    return response.data.data!;
  },

  async isSectionAccessible(sectionId: string): Promise<{
    isAccessible: boolean;
    requiresSequential: boolean;
    blockedReason?: string;
  }> {
    const response = await api.get<
      ApiResponse<{
        isAccessible: boolean;
        requiresSequential: boolean;
        blockedReason?: string;
      }>
    >(`/sections/${sectionId}/accessible`);
    return response.data.data!;
  },

  // ============================================
  // Final Exam (Estudiante)
  // ============================================

  async getFinalExamForStudent(courseId: string) {
    const response = await api.get(`/final-exams/course/${courseId}`);
    return response.data.data ?? null;
  },

  async startFinalExamAttempt(finalExamId: string) {
    const response = await api.post(`/final-exams/${finalExamId}/start`);
    return response.data.data!;
  },

  async saveFinalExamProgress(
    attemptId: string,
    savedAnswers: Record<string, string>,
  ): Promise<{ lastSavedAt: string }> {
    const response = await api.post<ApiResponse<{ lastSavedAt: string }>>(
      `/final-exams/attempt/${attemptId}/save-progress`,
      { savedAnswers },
    );
    return response.data.data!;
  },

  async submitFinalExamAttempt(
    attemptId: string,
    answers: Array<{ questionId: string; selectedOptionId: string }>,
  ) {
    const response = await api.post("/final-exams/submit", {
      attemptId,
      answers,
    });
    return response.data.data!;
  },

  async getFinalExamResult(attemptId: string) {
    const response = await api.get(`/final-exams/attempt/${attemptId}/result`);
    return response.data.data!;
  },
};
